/**
 * Extract structured fields (name, email, phone, location, linkedin, current_title)
 * from beautified resume text so the Add Candidate form can auto-fill.
 *
 * Runs entirely client-side after `/api/resumes/parse` returns the beautified
 * text. Only pre-fills fields that are currently empty on the form — never
 * overwrites what the recruiter has already typed.
 */

const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/;
const PHONE_RE = /(?<!\d)(\+?\d(?:[\s\-().]?\d){9,14})(?!\d)/;
const LINKEDIN_RE = /(?:https?:\/\/)?(?:www\.)?linkedin\.com\/(?:in|pub)\/[A-Za-z0-9\-_%]+\/?/i;

// Well-known Indian + international city keywords for a cheap location heuristic
const CITY_HINTS = [
    "Bengaluru", "Bangalore", "Mumbai", "Delhi", "Delhi NCR", "Noida", "Gurgaon", "Gurugram",
    "Pune", "Hyderabad", "Chennai", "Kolkata", "Ahmedabad", "Jaipur", "Nainital", "Chandigarh",
    "Kochi", "Ernakulam", "Thiruvananthapuram", "Bhubaneswar", "Indore", "Lucknow", "Nagpur",
    "Coimbatore", "Kanpur", "Bhopal", "Vadodara", "Surat", "Faridabad", "Ghaziabad",
    "Remote", "India", "US", "USA", "UK", "Canada", "Singapore",
];

function _pickCityLine(lines) {
    // Look through the first ~10 non-empty lines for a short line containing a city hint.
    // Also inspects lines that contain an email/phone — the city may share that line.
    let n = 0;
    for (const raw of lines) {
        const ln = (raw || "").trim();
        if (!ln) continue;
        n += 1;
        if (n > 10) break;
        if (ln.length > 120) continue;
        // Strip any email/phone from the line so we don't return them as location
        const cleaned = ln
            .replace(EMAIL_RE, " ")
            .replace(PHONE_RE, " ")
            .replace(/[|·•—–]/g, " ")
            .replace(/\s+/g, " ")
            .trim();
        if (!cleaned || cleaned.length > 80) continue;
        for (const c of CITY_HINTS) {
            const re = new RegExp(`\\b${c}\\b`, "i");
            if (re.test(cleaned)) {
                return cleaned.replace(/^[\s,·|·•—–-]+|[\s,·|·•—–-]+$/g, "");
            }
        }
    }
    return "";
}

// Well-known section headers we must never emit as `current_title`
const _TITLE_STOP_HEADERS = new Set([
    "EDUCATION", "SKILLS", "CONTACT", "EXPERIENCE", "WORK EXPERIENCE",
    "PROFESSIONAL EXPERIENCE", "EMPLOYMENT", "EMPLOYMENT HISTORY",
    "PROJECTS", "CERTIFICATIONS", "CERTIFICATES", "SUMMARY", "OBJECTIVE",
    "PROFILE", "ABOUT", "AWARDS", "ACHIEVEMENTS", "LANGUAGES",
    "INTERESTS", "HOBBIES", "REFERENCES", "STRENGTHS", "TECHNICAL SKILLS",
    "SOFT SKILLS", "TOOLS", "TECHNOLOGIES", "TRAININGS", "COURSES",
    "PERSONAL DETAILS", "KEY SKILLS", "CAREER OBJECTIVE", "CAREER SUMMARY",
    "AREAS OF EXPERTISE", "CORE COMPETENCIES", "PROFESSIONAL SUMMARY",
    "EXECUTIVE SUMMARY", "WORK HISTORY", "TEAM LEAD EXPERIENCE",
]);

// Feb 2026 prod bug fix — CVs whose first prominent word is a section header
// (e.g. "RESUME", "OBJECTIVE", "CURRICULUM VITAE") were being picked up as the
// candidate NAME. Also seen: Indian parentage lines like "S/o M.Velselvan"
// (Son of ...) being emitted as the name. Blacklist these outright.
const _NAME_BLACKLIST_UPPER = new Set([
    "RESUME", "CV", "CURRICULUM VITAE", "BIODATA", "BIO-DATA", "BIO DATA",
    "OBJECTIVE", "CAREER OBJECTIVE", "SUMMARY", "PROFESSIONAL SUMMARY",
    "EXECUTIVE SUMMARY", "CAREER SUMMARY", "PROFILE", "PERSONAL PROFILE",
    "ABOUT ME", "ABOUT", "CONTACT", "CONTACT INFO", "PERSONAL DETAILS",
    "PERSONAL INFORMATION", "PERSONAL INFO", "APPLICATION",
    "APPLICATION FORM", "PROFESSIONAL PROFILE", "MY DETAILS",
]);
// Indian "Son of / Daughter of / Wife of / Care of" prefixes — never a name.
const _PARENTAGE_PREFIX_RE = /^(S\/o|D\/o|W\/o|C\/o|s\/o|d\/o|w\/o|c\/o|Son\s+of|Daughter\s+of|Wife\s+of|Care\s+of|C\.?\s?O\.?|s\.o\.|d\.o\.)\b/i;

function _isBlacklistedName(ln) {
    if (!ln) return true;
    const upper = ln.replace(/[:.\s]+$/, "").toUpperCase().trim();
    if (_NAME_BLACKLIST_UPPER.has(upper)) return true;
    // Also reject if the line STARTS with a blacklisted header word
    // (e.g. "RESUME - John Doe" or "OBJECTIVE:" trailing text).
    for (const bad of _NAME_BLACKLIST_UPPER) {
        if (upper === bad || upper.startsWith(bad + " ") || upper.startsWith(bad + ":") || upper.startsWith(bad + "-")) {
            return true;
        }
    }
    if (_PARENTAGE_PREFIX_RE.test(ln)) return true;
    return false;
}

function _pickName(lines) {
    // Name is typically the first non-empty line, 1-5 words, mostly capitalised,
    // and does NOT contain an email/phone/URL.
    for (const raw of (lines || []).slice(0, 10)) {
        const ln = (raw || "").trim();
        if (!ln) continue;
        if (ln.length > 60) continue;
        if (EMAIL_RE.test(ln) || PHONE_RE.test(ln) || LINKEDIN_RE.test(ln)) continue;
        // Feb 2026 — reject resume/section headers and parentage lines.
        if (_isBlacklistedName(ln)) continue;
        const words = ln.split(/\s+/);
        if (words.length < 1 || words.length > 5) continue;
        // At least half the words should start with uppercase (allow all-caps too).
        const capCount = words.filter((w) => /^[A-Z][A-Za-z.'-]*$/.test(w)).length;
        if (capCount >= Math.ceil(words.length / 2)) {
            return ln;
        }
    }
    return "";
}

function _pickCurrentTitle(lines, name) {
    // Look for the line right after the name — often the role/title.
    if (!lines?.length) return "";
    let hitName = !name;
    for (const raw of lines.slice(0, 8)) {
        const ln = (raw || "").trim();
        if (!ln) continue;
        if (!hitName) {
            if (ln === name) hitName = true;
            continue;
        }
        const upper = ln.toUpperCase().replace(/:$/, "").trim();
        if (_TITLE_STOP_HEADERS.has(upper)) return "";
        if (_isBlacklistedName(ln)) continue;
        if (EMAIL_RE.test(ln) || PHONE_RE.test(ln)) continue;
        if (ln.length > 80) continue;
        const words = ln.split(/\s+/);
        if (words.length < 2 || words.length > 8) continue;
        // A section header masquerading — reject if line is entirely uppercase
        // (title lines are usually mixed-case).
        if (ln === ln.toUpperCase()) continue;
        return ln;
    }
    return "";
}

// ---- Skills extraction ------------------------------------------------------
const _SKILLS_HEADERS = /^\s*(SKILLS?|TECHNICAL SKILLS?|KEY SKILLS?|CORE SKILLS?|SOFT SKILLS?|TOOLS?|TECHNOLOGIES|EXPERTISE|CORE COMPETENC(?:Y|IES)|AREAS OF EXPERTISE)\s*:?\s*$/i;
const _ANY_SECTION_HEADER = /^\s*(EDUCATION|WORK EXPERIENCE|PROFESSIONAL EXPERIENCE|EXPERIENCE|EMPLOYMENT|PROJECTS|CERTIFICATIONS?|CERTIFICATES?|LANGUAGES?|INTERESTS?|HOBBIES|SUMMARY|OBJECTIVE|PROFILE|CONTACT|AWARDS?|ACHIEVEMENTS?|REFERENCES?|TEAM LEAD EXPERIENCE|AREAS OF EXPERTISE|COURSES|TRAININGS?|PERSONAL DETAILS|CAREER OBJECTIVE|WORK HISTORY)\s*:?\s*$/i;

function _pickSkills(lines) {
    if (!lines?.length) return [];
    let inSkills = false;
    const buf = [];
    for (const raw of lines) {
        const ln = (raw || "").trim();
        if (!ln) {
            if (inSkills && buf.length) break;
            continue;
        }
        if (_SKILLS_HEADERS.test(ln)) { inSkills = true; continue; }
        if (inSkills) {
            if (_ANY_SECTION_HEADER.test(ln)) break;
            buf.push(ln);
            if (buf.length >= 8) break;
        }
    }
    if (!buf.length) return [];
    const raw = buf.join(" • ").split(/\s*[,|;•·▪▫]\s*|(?:\s{2,})/).map((s) => s.trim()).filter(Boolean);
    const skills = raw.filter((s) => {
        if (s.length < 2 || s.length > 35) return false;
        if (_ANY_SECTION_HEADER.test(s)) return false;
        if (EMAIL_RE.test(s) || PHONE_RE.test(s)) return false;
        const words = s.split(/\s+/);
        if (words.length > 5) return false;
        if (words.length >= 4 && /[.!?]$/.test(s)) return false;
        return true;
    });
    const seen = new Set();
    const out = [];
    for (const s of skills) {
        const key = s.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(s);
    }
    return out.slice(0, 30);
}

// ---- Years of experience extraction ----------------------------------------
const _YEARS_PATTERNS = [
    /(\d{1,2})\+?\s*(?:years?|yrs?)\s*(?:of\s+)?(?:professional\s+)?(?:experience|exp\.?)/i,
    /(?:experience|exp\.?)[^.\n]{0,20}?(\d{1,2})\+?\s*(?:years?|yrs?)/i,
    /(?:with|over|total)\s+(\d{1,2})\+?\s*(?:years?|yrs?)/i,
    /(\d{1,2})\+?\s*(?:years?|yrs?)\s+in\b/i,
];

function _pickYearsExperience(text) {
    if (!text) return null;
    const head = text.slice(0, 600);
    for (const re of _YEARS_PATTERNS) {
        const m = head.match(re);
        if (m) {
            const n = parseInt(m[1], 10);
            if (Number.isFinite(n) && n >= 0 && n <= 60) return n;
        }
    }
    return null;
}


/**
 * Given beautified resume text, return a partial candidate form object with
 * detected fields. Caller merges into the form only for keys where the
 * existing form value is empty.
 */
export function extractCandidateFieldsFromResume(text) {
    if (!text || typeof text !== "string") return {};
    const lines = text.split(/\r?\n/);

    const emailMatch = text.match(EMAIL_RE);
    const phoneMatch = text.match(PHONE_RE);
    const linkedinMatch = text.match(LINKEDIN_RE);
    const name = _pickName(lines);
    const location = _pickCityLine(lines);
    const current_title = _pickCurrentTitle(lines, name);
    const skills = _pickSkills(lines);
    const years_experience = _pickYearsExperience(text);

    const out = {};
    if (name) out.name = name;
    if (emailMatch) out.email = emailMatch[0].toLowerCase();
    if (phoneMatch) out.phone = phoneMatch[0].replace(/\s+/g, " ").trim();
    if (location) out.location = location;
    if (current_title) out.current_title = current_title;
    if (linkedinMatch) {
        let li = linkedinMatch[0];
        if (!/^https?:\/\//i.test(li)) li = "https://" + li;
        out.linkedin = li;
    }
    if (skills.length > 0) out.skills = skills;
    if (years_experience != null) out.years_experience = years_experience;
    return out;
}

/**
 * Merge extracted fields into an existing form, only filling BLANK keys.
 * Numeric/zero values on `existingForm.years_experience` are preserved.
 * For array fields (skills), fills only when the existing array is empty.
 */
export function mergeAutoFill(existingForm, extracted) {
    const out = { ...existingForm };
    let touched = 0;
    for (const [k, v] of Object.entries(extracted || {})) {
        const cur = out[k];
        const isEmpty = cur === undefined || cur === null || cur === "" ||
            (Array.isArray(cur) && cur.length === 0) ||
            (typeof cur === "number" && cur === 0 && k === "years_experience");
        if (isEmpty && v != null && !(Array.isArray(v) && v.length === 0)) {
            out[k] = v;
            touched += 1;
        }
    }
    return { form: out, filledCount: touched };
}
